import React from "react";
import { Link } from "react-router-dom";
import FooterQuickLinks from "@/components/FooterQuickLinks";
import { BusinessContactInline, DBA_NAME } from "@/components/BusinessContact";

/**
 * Footer used across public pages. Groups links to all legal / policy /
 * contact pages behind the dog registry brand.
 *
 * `pb-28 md:pb-12` makes room for the fixed "Made with Emergent" badge
 * (bottom-right, 178×40) so it can't visually cover the bottom row of
 * footer links on mobile — D&B 2026-06-16 denial referenced exactly
 * this overlap.
 */
export default function PublicFooter() {
  return (
    <footer
      className="bg-[#0E0E0E] text-[#C4C4C4] mt-20 print:hidden pb-28 md:pb-0"
      data-testid="public-footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 grid gap-10 sm:grid-cols-2 md:grid-cols-4">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <img
              src="/logo-256.png"
              alt="Armstrong K9 Registry"
              className="h-10 w-10 rounded-full object-contain"
            />
            <div className="text-sm font-semibold text-white leading-tight">
              Armstrong's Supreme<br />Team K9 Registry
            </div>
          </div>
          <p className="text-xs leading-relaxed text-[#C4C4C4]/80">
            A pedigree registry for breeders, owners, and everyone who takes
            their dogs seriously. Built in the United States.
          </p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold mb-3">
            Product
          </p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/features" className="hover:text-white" data-testid="footer-features">Features</Link></li>
            <li><Link to="/seller-standards" className="hover:text-white" data-testid="footer-seller-standards">Seller standards</Link></li>
            <li><Link to="/buyer-rights" className="hover:text-white" data-testid="footer-buyer-rights">Buyer's bill of rights</Link></li>
            <li><Link to="/breeding" className="hover:text-white">Breeding directory</Link></li>
            <li><Link to="/events" className="hover:text-white">Events</Link></li>
            <li><Link to="/billing" className="hover:text-white">Plans &amp; pricing</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold mb-3">
            Legal
          </p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/terms" className="hover:text-white" data-testid="footer-terms">Terms of Service</Link></li>
            <li><Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">Privacy Policy</Link></li>
            <li><Link to="/refund" className="hover:text-white" data-testid="footer-refund">Refund Policy</Link></li>
            <li><Link to="/sub-processors" className="hover:text-white" data-testid="footer-subprocessors">Sub-processors</Link></li>
            <li><Link to="/consent" className="hover:text-white" data-testid="footer-consent">User Consent</Link></li>
            <li><Link to="/compliance" className="hover:text-white" data-testid="footer-compliance">Compliance</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold mb-3">
            Responsibility
          </p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/ethics" className="hover:text-white" data-testid="footer-ethics">Ethical Considerations</Link></li>
            <li><Link to="/accessibility" className="hover:text-white" data-testid="footer-accessibility">Accessibility Statement</Link></li>
            <li><Link to="/security" className="hover:text-white" data-testid="footer-security">Security Measures</Link></li>
            <li><Link to="/status" className="hover:text-white" data-testid="footer-status">System status</Link></li>
            <li><Link to="/contact" className="hover:text-white" data-testid="footer-contact">Contact Us</Link></li>
          </ul>
        </div>
      </div>
      <FooterQuickLinks tone="dark" />
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 text-[11px] text-[#C4C4C4]/60 leading-relaxed" data-testid="usda-disclaimer">
          <strong className="text-gold">Animal welfare disclosure:</strong> Buyers should
          verify USDA APHIS license for any breeder selling more than four
          litters per calendar year. Livestock and pet sale rules vary by US
          state — confirm local laws before completing a purchase. Restricted
          and banned-breed regulations (UK / Germany / Australia and several US
          municipalities) are the buyer's responsibility to verify. Armstrong's
          Armstrong K9 Registry is a venue, not the seller of any listed dog.
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#C4C4C4]/70">
          <p data-testid="footer-legal-entity">© {new Date().getFullYear()} {DBA_NAME}. All rights reserved.</p>
          {/* Single source of truth for email + phone + contact form,
              shown LEFT-aligned on mobile so the fixed Emergent badge
              in the bottom-right corner can't visually cover it. */}
          <BusinessContactInline tone="dark" />
        </div>
      </div>
    </footer>
  );
}
